import { z } from "zod";
import { AgentIdSchema } from "./primitives.js";

/**
 * Usage metering RPC + the additive `usage` TurnEvent shape.
 *
 * `usage.summary` (Hub → Gateway) returns calendar-month totals and per-day /
 * per-agent / per-user / per-model aggregates over the Gateway-local
 * `usage_events` store. Token counts are provider-reported (exact); dollar
 * figures are *estimated* — summed from snapshotted per-call cost, which is
 * null for calls whose model had neither registry nor override pricing.
 *
 * `UsageTurnEventSchema` documents the wire shape PI emits for every completed
 * LLM call. The TurnEvent stream pi-agent → gateway is an internal seam with
 * no Zod validator, so this schema is authored for the contracts-test suite
 * and to keep the field set in one canonical place; the gateway's TurnEvent
 * union mirrors it.
 */

// ---------------------------------------------------------------------------
// usage TurnEvent (PI Agent → Gateway, additive, one per completed LLM call)
// ---------------------------------------------------------------------------

export const UsageTurnEventSchema = z
  .object({
    type: z.literal("usage"),
    /** Unique per completed LLM call; the dedup key on the write path. */
    callId: z.string().min(1),
    /** Resolved model id, e.g. "openai/gpt-5.4". */
    model: z.string().min(1),
    input: z.number().int().nonnegative(),
    output: z.number().int().nonnegative(),
    cacheRead: z.number().int().nonnegative(),
    cacheWrite: z.number().int().nonnegative(),
    /**
     * Registry-priced cost in USD, or null when the resolved model has no
     * registry pricing. The gateway may still gap-fill from a local override
     * file at write time, so null here does not imply the persisted row is
     * unpriced.
     */
    costUsd: z.number().nonnegative().nullable(),
  })
  .strict();
export type UsageTurnEvent = z.infer<typeof UsageTurnEventSchema>;

// ---------------------------------------------------------------------------
// usage.summary request
// ---------------------------------------------------------------------------

export const CURRENT_MONTH_USAGE_WINDOW = {
  window: "current-calendar-month",
} as const;

export const UsageSummaryPayloadSchema = z.discriminatedUnion("window", [
  z
    .object({
      window: z.literal(CURRENT_MONTH_USAGE_WINDOW.window),
      agentIds: z.array(AgentIdSchema).optional(),
    })
    .strict(),
  z
    .object({
      window: z.literal("calendar-month"),
      /** A current or past month, resolved in the Gateway's configured timezone. */
      month: z.string().regex(/^[1-9]\d{3}-(0[1-9]|1[0-2])$/),
      agentIds: z.array(AgentIdSchema).optional(),
    })
    .strict(),
]);
export type UsageSummaryPayload = z.infer<typeof UsageSummaryPayloadSchema>;

// ---------------------------------------------------------------------------
// usage.summary response
// ---------------------------------------------------------------------------

const UsageTotalsSchema = z.object({
  /** Sum over priced calls only. */
  costUsd: z.number().nonnegative(),
  input: z.number().int().nonnegative(),
  output: z.number().int().nonnegative(),
  cacheRead: z.number().int().nonnegative(),
  cacheWrite: z.number().int().nonnegative(),
  llmCalls: z.number().int().nonnegative(),
  sessions: z.number().int().nonnegative(),
  /** Calls excluded from costUsd because their model had no price anywhere. */
  unpricedCalls: z.number().int().nonnegative(),
});

const UsageReportWindowSchema = z.object({
  /** Explicit requests for the current month also resolve to current-calendar-month. */
  selector: z.enum(["current-calendar-month", "calendar-month"]),
  timezone: z.string(),
  startTs: z.string(),
  endTs: z.string(),
  priorStartTs: z.string(),
  priorEndTs: z.string(),
});

const UsageByDaySchema = z.object({
  date: z.string(),
  costUsd: z.number().nonnegative(),
  llmCalls: z.number().int().nonnegative(),
});

const UsageByAgentDaySchema = z.object({
  date: z.string(),
  agentId: z.string(),
  costUsd: z.number().nonnegative(),
});

const UsageByAgentSchema = z.object({
  agentId: z.string(),
  costUsd: z.number().nonnegative(),
  input: z.number().int().nonnegative(),
  output: z.number().int().nonnegative(),
  cacheRead: z.number().int().nonnegative(),
  cacheWrite: z.number().int().nonnegative(),
  llmCalls: z.number().int().nonnegative(),
  sessions: z.number().int().nonnegative(),
  unpricedCalls: z.number().int().nonnegative(),
});

/**
 * The background producer that started a turn nobody sent. Rows written before
 * origins were recorded carry null and stay in the "Unattributed" bucket.
 */
export const UsageOriginSchema = z.enum(["schedule", "workflow", "async-job"]);
export type UsageOrigin = z.infer<typeof UsageOriginSchema>;

const UsageByUserSchema = z.object({
  /** A null triple marks a row nobody sent: a background origin, or "Unattributed". */
  platform: z.string().nullable(),
  userId: z.string().nullable(),
  displayName: z.string().nullable(),
  /** Set only on rows nobody sent; a person's row always carries null. */
  origin: UsageOriginSchema.nullable(),
  costUsd: z.number().nonnegative(),
  input: z.number().int().nonnegative(),
  output: z.number().int().nonnegative(),
  cacheRead: z.number().int().nonnegative(),
  cacheWrite: z.number().int().nonnegative(),
  llmCalls: z.number().int().nonnegative(),
  sessions: z.number().int().nonnegative(),
});

const UsageByModelSchema = z.object({
  model: z.string(),
  /** Sums priced calls; a model can be part-priced within one period. */
  costUsd: z.number().nonnegative(),
  llmCalls: z.number().int().nonnegative(),
  unpricedCalls: z.number().int().nonnegative(),
});

/**
 * Current-month spend-cap state, present only when a cap is configured.
 * Historical reports omit it because past cap settings are not retained.
 * Its period is the same window as every figure in the response.
 */
const UsageBudgetSchema = z.object({
  capUsd: z.number().positive(),
  /** IANA zone whose calendar month defines the period. */
  timezone: z.string(),
  /** Inclusive ISO instant at which the period starts. */
  periodStart: z.string(),
  /** Exclusive ISO instant at which the period ends. */
  periodEnd: z.string(),
  /**
   * Recorded spend for the period, summed from the same snapshotted per-call
   * cost as `totals.costUsd`, so unpriced calls contribute $0. Null only when
   * `state` is `degraded`.
   */
  spentUsd: z.number().nonnegative().nullable(),
  /** `capUsd - spentUsd`; negative once admitted turns overshoot. Null when degraded. */
  remainingUsd: z.number().nullable(),
  /**
   * `ok` admits new turns; `blocked` rejects them because recorded spend
   * reached the cap. `degraded` means reporting cannot trust the ledger; a
   * read failure refuses admission, while a dropped write is reported until a
   * successful usage write recovers it.
   */
  state: z.enum(["ok", "blocked", "degraded"]),
});

export const UsageSummaryDataSchema = z.object({
  window: UsageReportWindowSchema,
  totals: UsageTotalsSchema,
  priorTotals: UsageTotalsSchema,
  byDay: z.array(UsageByDaySchema),
  byAgentDay: z.array(UsageByAgentDaySchema),
  byAgent: z.array(UsageByAgentSchema),
  byUser: z.array(UsageByUserSchema),
  byModel: z.array(UsageByModelSchema),
  /**
   * ISO timestamp of the newest usage event for the requested agents,
   * independent of the date range; null when none exist. Backs the page's
   * "data through …" freshness caption.
   */
  dataThrough: z.string().nullable(),
  budget: UsageBudgetSchema.optional(),
});
export type UsageSummaryData = z.infer<typeof UsageSummaryDataSchema>;

export type UsageByDay = z.infer<typeof UsageByDaySchema>;
export type UsageByAgentDay = z.infer<typeof UsageByAgentDaySchema>;
export type UsageByAgent = z.infer<typeof UsageByAgentSchema>;
export type UsageByUser = z.infer<typeof UsageByUserSchema>;
export type UsageByModel = z.infer<typeof UsageByModelSchema>;
export type UsageTotals = z.infer<typeof UsageTotalsSchema>;
export type UsageReportWindow = z.infer<typeof UsageReportWindowSchema>;
export type UsageBudget = z.infer<typeof UsageBudgetSchema>;
